import * as yup from 'yup'

export const getCompanySchema = (__: (key: string) => string) => {
  return yup.object().shape({
    file: yup.mixed().required(__('File is required'))
  })
}

export const getCompanyCreateSchema = (__: (key: string) => string) => {
  return yup.object().shape({
    name: yup
      .string()
      .required(__('Company name is required'))
      .test('no-leading-space', __('Company name cannot start with a space'), value => {
        if (!value) return true

        return value[0] !== ' '
      }),
    short_name: yup
      .string()
      .required(__('Short name is required'))
      .max(50, __('Company short name cannot exceed 50 characters'))
      .test('no-leading-space', __('Company short name cannot start with a space'), value => {
        if (!value) return true

        return value[0] !== ' '
      }),
    business_number: yup
      .string()
      .required(__('Business registration is required'))
      .matches(/^\d+$/, __('Business registration must be a number')),
    bank_account_number: yup
      .string()
      .required(__('Bank account number is required'))
      .test('no-leading-space', __('Bank account number cannot start with a space'), value => {
        if (!value) return true

        return value[0] !== ' '
      }),
    phone: yup
      .string()
      .nullable()
      .test('phone-format', __('Invalid phone number'), function (value) {
        if (!value) {
          return true
        }

        return /^\d{10,14}$/.test(value)
      }),

    bank_id: yup.number().nullable().typeError(__('Bank Name is required')).required(__('Bank Name is required')),

    closing_date: yup
      .number()
      .typeError(__('Closing date must be a valid number'))
      .required(__('Closing date is required'))
      .integer(__('Closing date must be an integer'))
      .when('closing_specific_date', {
        is: 'enter_date',
        then: schema =>
          schema
            .max(28, __('Closing date allowed is 28 days'))
            .min(0, __('Closing date must be greater than or equal to 0')),
        otherwise: schema => schema
      }),

    closing_specific_date: yup.string().typeError(__('Option is required')).required(__('Option is required')),

    closing_date_type: yup.string().when('closing_specific_date', {
      is: 'enter_date',
      then: schema =>
        schema.typeError(__('Closing date type is required')).required(__('Closing date type is required')),
      otherwise: schema => schema.nullable()
    }),

    minimum_working_days: yup
      .number()
      .typeError(__('Minimum working days must be a valid number'))
      .required(__('Minimum working days is required'))
      .min(0, __('Minimum working days must be greater than or equal to 0'))
      .integer(__('Minimum working days must be an integer'))
      .max(30, __('Maximum allowed is 30 days')),

    withdraw_cutoff_day: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return null
        }

        return value
      })
      .nullable()
      .when('withdraw_cutoff_option', {
        is: 'enter_date',
        then: schema =>
          schema
            .typeError(__('Withdraw cutoff day must be a valid number'))
            .required(__('Withdraw cutoff day is required'))
            .min(1, __('Withdraw cutoff day must be between 1 and 30'))
            .max(30, __('Withdraw cutoff day must be between 1 and 30'))
            .integer(__('Withdraw cutoff day must be an integer')),
        otherwise: schema => schema.nullable()
      }),
    withdraw_cutoff_option: yup
      .string()
      .typeError(__('Withdraw cutoff option is required'))
      .required(__('Withdraw cutoff option is required')),

    memo: yup.string().typeError(__('Memo is required')).required(__('Memo is required'))
  })
}

export const getUploadContractSchema = (__: (key: string) => string) => {
  return yup.object().shape({
    contract_file: yup
      .mixed()
      .required(__('File is required'))
      .test('file-size', __('File uploads must be under 50MB.'), function (value) {
        if (!value) return true

        let file: File | null = null

        if (value instanceof FileList && value.length > 0) {
          file = value[0]
        } else if (value instanceof File) {
          file = value
        }

        if (file && file.size) {
          const maxSize = 50 * 1024 * 1024

          return file.size <= maxSize
        }

        return true
      }),
    start_date: yup
      .string()
      .required(__('Contract start date is required'))
      .test('not-future', __('Start date cannot be in the future'), function (value) {
        if (!value) return true

        const startDate = new Date(value)
        const today = new Date()

        today.setHours(0, 0, 0, 0)
        startDate.setHours(0, 0, 0, 0)

        return startDate <= today
      }),
    end_date: yup
      .string()
      .required(__('Contract end date is required'))
      .test('is-greater', __('End date must be after start date'), function (value) {
        const { start_date } = this.parent

        if (!start_date || !value) return true

        return new Date(value) > new Date(start_date)
      })
  })
}
